import { array } from 'fp-ts';
import { keepPreviousData } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import {
    useRepositoryQuery,
    PortfolioRateOverview,
    PortfolioRateOverviewFilters,
    useRepositoryMutation,
    InvalidateQueries,
} from '@mews-commander/data-access';
import { MewsTeam } from '@mews/enums';
import { TableState } from '@optimus-web/core';
import { getCurrentTargetEnterpriseIds } from '@mews-commander/legacy-entities/src/ducks/enterpriseRelationships';

const queriesToInvalidate: InvalidateQueries = {
    rates: ['getAll'],
    portfolioRates: ['getOverviewEntries'],
    portfolioCentralRateGroups: ['getOverview', 'getAll'],
    portfolioRateGroups: ['getOverview'],
};

interface UseRateIntersectionParams {
    enabled?: boolean;
    enterpriseIds: string[];
    voucherIds?: string[];
}

export const useRateCreate = () =>
    useRepositoryMutation('rates', 'bulkAdd', { invalidateQueries: queriesToInvalidate });

export const useDeleteRates = () =>
    useRepositoryMutation('portfolioRates', 'delete', {
        invalidateQueries: queriesToInvalidate,
        meta: {
            logError: {
                team: MewsTeam.Pricing,
            },
        },
    });

export const useRateEdit = () =>
    useRepositoryMutation('rates', 'bulkUpdate', { invalidateQueries: queriesToInvalidate });

export const useRateByIds = (enterpriseIds: string[], rateIds?: string[], options?: { enabled?: boolean }) =>
    useRepositoryQuery(
        'rates',
        'getAll',
        { enterpriseIds, rateIds, extent: { pricings: true } },
        {
            placeholderData: keepPreviousData,
            enabled: options?.enabled ?? true,
        }
    );

export const useRateIntersection = ({ enterpriseIds, voucherIds, enabled = true }: UseRateIntersectionParams) =>
    useRepositoryQuery(
        'rates',
        'getIntersectedKeys',
        { enterpriseIds, voucherIds },
        {
            enabled: enabled && (array.isNonEmpty(enterpriseIds) || (!!voucherIds && array.isNonEmpty(voucherIds))),
        }
    );

export const useRates = (
    tableState: TableState<PortfolioRateOverview, PortfolioRateOverviewFilters>,
    rateFilters?: PortfolioRateOverviewFilters
) => {
    const enterpriseIds = useSelector(getCurrentTargetEnterpriseIds);

    return useRepositoryQuery(
        'portfolioRates',
        'getOverviewEntries',
        {
            enterpriseIds,
            ...tableState,
            filters: {
                ...rateFilters,
                ...tableState.filters,
            },
        },
        {
            placeholderData: keepPreviousData,
        }
    );
};
